import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  ButtonV2,
  DatePickerV2,
  InputStartAdornment,
  NoteV2,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  TextInputV4,
  labraUIDateFormat,
} from '@labrav/react-components'
import { get, isNull, isUndefined } from 'lodash'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useMemo, useState } from 'react'
import { getRandom } from '@labrav/common'
import clsx from 'clsx'
import { DateTime } from 'luxon'
import { getScheduleTypeOptions } from '../../../common/utils/productCreationUtil'
import { Installments, ScheduleType } from '../../../modules/privateOffer/types'
import { getAmiInstallmentsBasedOnSchedule } from '../../../utils/amiPrivateOfferUtil'
import { getCurrencySymbol } from '../../../common/utils/getCurrencySymbol'
import { awsMaxScheduledInstallments } from '../../../common/utils/constants'
import { awsMonthlyInstallmentsBannerMessage } from '../../../common/utils/messagesContants'
import { useStyles } from './InstallmentsSection.styles'

export interface InstallmentsSectionProps {
  installments?: Installments[]
  tcv?: number
  durationInMonths: number
  firstInstallmentDate?: string
  handleChangeInstallmentsAmount?: (installments: Installments[]) => void
  isResale?: boolean
  minDate?: string
  maxDate?: string
  validate?: boolean
}

export const InstallmentsSection = ({
  tcv,
  installments,
  durationInMonths,
  firstInstallmentDate,
  handleChangeInstallmentsAmount,
  isResale = false,
  minDate,
  maxDate,
  validate = false,
}: InstallmentsSectionProps) => {
  const { classes } = useStyles()
  const formik = useFormikContext() as FormikProps<Record<string, unknown>>
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    handleBlur,
    errors,
  } = formik
  const currency = (values?.currency as string) || 'USD'
  const key = isResale ? 'installmentInfo' : 'installments'
  const emptyResourse = {
    amount: null,
    paymentDate: '',
    currency: currency,
  }
  const paymentSchedule = values.flexiblePaymentType

  const [randomKey] = useState<number>(getRandom())

  const scheduleTypeOptions: RadioButtonGroupMapping[] =
    getScheduleTypeOptions(false)

  const checkedValue = useMemo(() => {
    switch (paymentSchedule) {
      case ScheduleType.QUARTERLY:
        return 'Quarterly'
      case ScheduleType.MONTHLY:
        return 'Monthly'
      case ScheduleType.YEARLY:
        return 'Yearly'
      case ScheduleType.CUSTOM:
        return 'Custom'
      default:
        return 'Monthly'
    }
  }, [paymentSchedule])

  // Calculate TCV from existing installments by summing all amounts
  const calculateTcvFromInstallments = (
    installmentsArray: Installments[] | undefined
  ): number => {
    if (!isUndefined(tcv)) {
      return tcv
    }
    if (!installmentsArray || installmentsArray.length === 0) {
      return tcv || 0
    }
    return installmentsArray.reduce((acc, curr) => {
      const amount = curr.amount
      if (!isUndefined(amount) && !isNull(amount)) {
        return acc + Number(amount)
      }
      return acc
    }, 0)
  }

  const handleSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedOptionKey = (event.target as HTMLInputElement).value as string
    const updateField = 'flexiblePaymentType'

    if (!selectedOptionKey) {
      await setFieldValue(updateField, values.flexiblePaymentType)
      return
    }
    const calculatedTcv = calculateTcvFromInstallments(installments)
    await setFieldValue('tcv', calculatedTcv)

    if (selectedOptionKey === 'Quarterly') {
      await setFieldValue(updateField, ScheduleType.QUARTERLY)
      const newInstallments = getAmiInstallmentsBasedOnSchedule({
        tcv: calculatedTcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.QUARTERLY,
        now: firstInstallmentDate || new Date().toISOString().split('T')[0],
      }).map(installment => ({
        ...installment,
        currency: currency,
      }))
      await setFieldValue(key, newInstallments)
    } else if (selectedOptionKey === 'Monthly') {
      await setFieldValue(updateField, ScheduleType.MONTHLY)
      const newInstallments = getAmiInstallmentsBasedOnSchedule({
        tcv: calculatedTcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.MONTHLY,
        now: firstInstallmentDate || new Date().toISOString().split('T')[0],
      }).map(installment => ({
        ...installment,
        currency: currency,
      }))
      await setFieldValue(key, newInstallments)
    } else if (selectedOptionKey === 'Yearly') {
      await setFieldValue(updateField, ScheduleType.YEARLY)
      const newInstallments = getAmiInstallmentsBasedOnSchedule({
        tcv: calculatedTcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.YEARLY,
        now: firstInstallmentDate || new Date().toISOString().split('T')[0],
      }).map(installment => ({
        ...installment,
        currency: currency,
      }))
      await setFieldValue(key, newInstallments)
    } else if (selectedOptionKey === 'Custom') {
      await setFieldValue(updateField, ScheduleType.CUSTOM)
      const newInstallments = getAmiInstallmentsBasedOnSchedule({
        tcv: calculatedTcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.CUSTOM,
        now: firstInstallmentDate || new Date().toISOString().split('T')[0],
      }).map(installment => ({
        ...installment,
        currency: currency,
      }))
      await setFieldValue(key, newInstallments)
    }
  }

  const handleChangeAmount = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      indexSchedule: number
    ) => {
      const value = (event.target as HTMLInputElement).value
      await setFieldValue(
        `${key}[${indexSchedule}].amount`,
        value !== '' ? Number(value) : value
      )
      if (handleChangeInstallmentsAmount && installments) {
        const updatedInstallments = [...installments]
        updatedInstallments[indexSchedule].amount =
          value !== '' ? Number(value) : null
        await handleChangeInstallmentsAmount(updatedInstallments)
      }
    },
    [setFieldValue, installments, handleChangeInstallmentsAmount]
  )

  const showDelete =
    (installments || []).length > 1 && paymentSchedule === ScheduleType.CUSTOM

  const totalAmount = useMemo(
    () =>
      (installments || []).reduce((acc, curr) => {
        const amount = Number(curr.amount)
        if (typeof amount === 'number' && !isNaN(amount)) {
          return acc + amount
        }
        return acc
      }, 0),
    [installments]
  )

  const installmentsCount = (installments || []).length

  return (
    <div
      className={classes.installmentsWrapper}
      data-testid={'installment-section'}
    >
      <div className={classes.sectionHeading}>Installment schedule</div>
      <div className={classes.radioSelection}>
        <RadioButtonsV2
          checkedValue={{
            key: checkedValue,
            value: checkedValue,
          }}
          name={`${key}.flexiblePaymentType`}
          radioButtonMappings={scheduleTypeOptions}
          handleChange={handleSelection}
          gapBetween="20px"
        />
      </div>
      {paymentSchedule === ScheduleType.MONTHLY && (
        <NoteV2 description={awsMonthlyInstallmentsBannerMessage} />
      )}
      <div className={clsx(classes.root, classes.elementsGap)}>
        <div className={classes.subSectionContainer}>
          <div className={classes.titleWraper}>
            <Typography className={clsx(classes.sectionText)}>
              Installment amount
            </Typography>
            <Typography className={clsx(classes.sectionText)}>
              Invoice date
            </Typography>
            {showDelete && <div className={classes.deleteSpacer}></div>}
          </div>
          <div className={classes.border}></div>
        </div>
        <FieldArray name={key} key={key}>
          {({ push, remove }) => (
            <>
              {(installments || []).map((installment, installmentsIndex) => {
                const handleDateChange = async (date: string | null) => {
                  const updatedDate = date
                    ? (DateTime.fromISO(date).toISO() || '').split('T')[0]
                    : ''

                  await setFieldValue(
                    `${key}[${installmentsIndex}].paymentDate`,
                    updatedDate
                  )
                  await setFieldTouched(
                    `${key}[${installmentsIndex}].paymentDate`,
                    true
                  )

                  // Sync with firstInstallmentDate for the first installment
                  if (installmentsIndex === 0) {
                    await setFieldValue('firstInstallmentDate', updatedDate)
                  }
                  // Trigger validation for the installments array to ensure array-level validation runs
                  await setFieldTouched(key, true)
                }
                return (
                  <>
                    <div
                      className={classes.resourceRow}
                      data-testid="installments-row"
                    >
                      <div className={classes.resourcesContainer}>
                        <div
                          className={classes.input}
                          key={`${key}-${randomKey}-${installmentsIndex}-amount`}
                        >
                          <TextInputV4
                            keyName={`${key}[${installmentsIndex}].amount`}
                            title="Installment amount"
                            type="number"
                            placeHolder=""
                            startAdornment={
                              <InputStartAdornment
                                text={getCurrencySymbol(currency) || '$'}
                              />
                            }
                            alignRight
                            value={installment?.amount as number}
                            updatedValue={installment?.amount as number}
                            isError={
                              !!(
                                get(
                                  touched,
                                  `${key}[${installmentsIndex}].amount`,
                                  false
                                ) &&
                                get(
                                  errors,
                                  `${key}[${installmentsIndex}].amount`,
                                  false
                                )
                              )
                            }
                            errorText={
                              (!!get(
                                touched,
                                `${key}[${installmentsIndex}].amount`,
                                false
                              ) &&
                                get(
                                  errors,
                                  `${key}[${installmentsIndex}].amount`,
                                  ''
                                )) ||
                              ''
                            }
                            onChange={event =>
                              handleChangeAmount(event, installmentsIndex)
                            }
                            isReadOnly={false}
                            isRequired={false}
                            showSpinner={false}
                            onBlur={handleBlur}
                            keyToPrevent={['+', '-', 'e']}
                          />
                        </div>
                        <div
                          className={classes.input}
                          key={`${key}-${randomKey}-${installmentsIndex}-paymentDate`}
                        >
                          <DatePickerV2
                            format={labraUIDateFormat}
                            views={['year', 'month', 'day']}
                            size="small"
                            field={{
                              title: 'Invoice date',
                              humanReadableKey: 'Invoice date',
                              placeHolder: 'Invoice date',
                              id: `${key}[${installmentsIndex}].paymentDate`,
                              isRequired: false,
                              isReadOnly: false,
                              isCustom: false,
                              dataType: 'date',
                              minDate: minDate
                                ? minDate
                                : DateTime.now().plus({ days: 1 }).toISODate(),
                              maxDate: maxDate ? maxDate : '',
                            }}
                            formik={formik}
                            fieldId={`${key}[${installmentsIndex}].paymentDate`}
                            handleDateChange={handleDateChange}
                            showErrorHelper={
                              !!(
                                !!get(
                                  touched,
                                  `${key}[${installmentsIndex}].paymentDate`,
                                  false
                                ) &&
                                get(
                                  errors,
                                  `${key}[${installmentsIndex}].paymentDate`,
                                  ''
                                )
                              )
                            }
                            errorHelperText={
                              (!!get(
                                touched,
                                `${key}[${installmentsIndex}].paymentDate`,
                                false
                              ) &&
                                get(
                                  errors,
                                  `${key}[${installmentsIndex}].paymentDate`,
                                  ''
                                )) ||
                              ''
                            }
                            validate={validate}
                          />
                        </div>
                      </div>
                      {showDelete ? (
                        <div className={classes.trashIcon}>
                          {installmentsIndex === 0 ? (
                            <div className={classes.deleteSpacer}></div>
                          ) : (
                            <FontAwesomeIcon
                              icon={faTrash}
                              className={classes.trashIconGlyph}
                              data-testid="trash-installments"
                              onClick={() => remove(installmentsIndex)}
                            />
                          )}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </>
                )
              })}
              {paymentSchedule === ScheduleType.CUSTOM &&
              (installments || [])?.length <=
                awsMaxScheduledInstallments - 1 ? (
                <div>
                  <div className={classes.belowBorder}></div>
                  <ButtonV2
                    className={classes.widthButton}
                    styleType="link"
                    onClick={() => {
                      push(emptyResourse)
                    }}
                    data-testid="add-a-installments-button"
                  >
                    Add more installments
                  </ButtonV2>
                  <Typography className={classes.buttonSubText}>
                    You can add{' '}
                    {awsMaxScheduledInstallments - (installments || [])?.length}{' '}
                    more installments.
                  </Typography>
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </FieldArray>
      </div>
      {installmentsCount > 0 && (
        <div className={clsx(classes.root, classes.metricsRow)}>
          <div className={classes.metricItem} data-testid="total-price">
            <Typography className={classes.sectionText}>
              Total amount
            </Typography>
            <Typography className={classes.totalText} data-testid="price">
              {getCurrencySymbol(currency) || '$'}
              {totalAmount}
            </Typography>
          </div>
          <div className={classes.metricItem} data-testid="installment-count">
            <Typography className={classes.sectionText}>
              Number of installments
            </Typography>
            <Typography
              className={classes.totalText}
              data-testid="installment-count-value"
            >
              {installmentsCount}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}
